import React, { useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import classNames from 'classnames';
import Lottie from 'lottie-react';
import './App.css';
import 'react-toastify/dist/ReactToastify.css';
import { RegistrationData } from './types/index';
import confettiAnimation from './assets/Confetti.json';
import GoogleSheetsService from './services/googleSheets';

function App() {
  const [formData, setFormData] = useState<RegistrationData>({
    hoTen: '',
    soDienThoai: '',
    linkShop: '',
    email: '',
    vanDeGapPhai: '',
    timestamp: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const appClasses = classNames('App', {
    // Có thể thêm các class conditional ở đây
  });

  // Function to validate email
  const isEmail = (input: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(input.trim());
  };

  // Function to validate Vietnamese phone number
  const isPhoneNumber = (input: string): boolean => {
    const cleanInput = input.replace(/\D/g, '');
    // Vietnamese phone numbers: 03x, 05x, 07x, 08x, 09x (10 digits)
    // Or +84 followed by 9 digits
    const vietnamPhoneRegex = /^(0[3|5|7|8|9][0-9]{8}|(\+84|84)[3|5|7|8|9][0-9]{8})$/;
    return vietnamPhoneRegex.test(cleanInput);
  };

  // Function to validate Shopee URL
  const isValidUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      // Check if it's a Shopee URL
      return urlObj.hostname.includes('shopee.vn') || 
             urlObj.hostname.includes('shopee.com');
    } catch {
      return false;
    }
  };

  // Function to validate form
  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    // Validate Họ và tên
    if (!formData.hoTen.trim()) {
      newErrors.hoTen = 'Vui lòng nhập họ và tên';
    } else if (formData.hoTen.trim().length < 2) {
      newErrors.hoTen = 'Họ và tên phải có ít nhất 2 ký tự';
    }

    // Validate Số điện thoại
    if (!formData.soDienThoai.trim()) {
      newErrors.soDienThoai = 'Vui lòng nhập số điện thoại';
    } else if (!isPhoneNumber(formData.soDienThoai)) {
      newErrors.soDienThoai = 'Số điện thoại Việt Nam không hợp lệ (03x, 05x, 07x, 08x, 09x)';
    }

    // Validate Email (optional)
    if (formData.email.trim() && !isEmail(formData.email)) {
      newErrors.email = 'Email không đúng định dạng';
    }

    // Link Shop - no validation needed (can input anything)

    // Validate Vấn đề gặp phải
    if (!formData.vanDeGapPhai.trim()) {
      newErrors.vanDeGapPhai = 'Vui lòng mô tả vấn đề gặp phải';
    } else if (formData.vanDeGapPhai.trim().length < 2) {
      newErrors.vanDeGapPhai = 'Vui lòng mô tả chi tiết hơn (ít nhất 2 ký tự)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof RegistrationData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    
    // Validate form
    if (!validateForm()) {
      toast.error('Vui lòng kiểm tra lại thông tin đã nhập!');
      return;
    }

    setIsLoading(true);
    
    try {
      const registrationData: RegistrationData = {
        ...formData,
        timestamp: new Date().toISOString()
      };

      console.log('Submitting registration data:', registrationData);
      
      const success = await GoogleSheetsService.getInstance().submitRegistration(registrationData);
      
      console.log('Registration success:', success);
      
      if (success) {
        setIsSubmitted(true);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
        
        toast.success('🎉 Đăng ký thành công! Thông tin của bạn đã được lưu vào hệ thống!', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        throw new Error('Đăng ký không thành công');
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Có lỗi xảy ra khi đăng ký. Vui lòng thử lại sau!');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetForm = () => {
    console.log('Reset form clicked!'); // Debug log
    setFormData({
      hoTen: '',
      soDienThoai: '',
      linkShop: '',
      email: '',
      vanDeGapPhai: '',
      timestamp: ''
    });
    setErrors({});
    setIsSubmitted(false);
    console.log('Form reset completed!'); // Debug log
  };

  return (
    <Router>
      <div className={appClasses}>
        {/* Confetti Animation */}
        {showConfetti && (
          <div className="confetti-overlay">
            <Lottie 
              animationData={confettiAnimation}
              loop={false}
              autoplay={true}
              style={{ 
                width: '100%', 
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 99999
              }}
            />
            {/* Fallback CSS confetti for mobile */}
            <div className="css-confetti">
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
            </div>
          </div>
        )}
        {/* Logo Section */}
        <div className="logo">
          <div className="logo-placeholder">
            <div className="logo-main">
              <img src="/assets/logos.png" alt="TUKI GROUP" />
            </div>
          </div>
        </div>

        {/* Event Info */}
        <div className="event-info">
          <img 
            src="/assets/AiEcommerce.png" 
            alt="AI E-COMMERCE REVOLUTION" 
            className="event-title-image" 
          />
        </div>

        {/* Registration Container */}
        <div className="checkin-container">
          {!isSubmitted ? (
            <>
              <div className="checkin-title">
                <h4>NHẬN TƯ VẤN KHÁM SHOP MIỄN PHÍ</h4>
                <div className="copyright-notice">
                  TÀI LIỆU THUỘC BẢN QUYỀN CỦA TUKGROUP TẶNG NHÀ BÁN HÀNG, VUI LÒNG KHÔNG SAO CHÉP VÀ LẤY PHÍ DƯỚI BẤT KỲ HÌNH THỨC NÀO.<br/><br/>
                  Vui lòng điền đầy đủ thông tin chính xác để nhận được sự hỗ trợ 1:1 từ TUKI nha ạ!
                </div>
              </div>
              <form className="checkin-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">Họ và tên</label>
                  <input
                    type="text"
                    className="checkin-input"
                    placeholder="Nhập tên của bạn"
                    value={formData.hoTen}
                    onChange={e => handleInputChange('hoTen', e.target.value)}
                    disabled={isLoading}
                    required
                  />
                  {errors.hoTen && (
                    <div className="form-error">
                      <span className="form-error-icon">⚠️</span>
                      <span>{errors.hoTen}</span>
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label className="form-label">Số điện thoại</label>
                  <div className="form-help-text">Có sử dụng zalo</div>
                  <input
                    type="tel"
                    className="checkin-input"
                    placeholder="Nhập sđt của bạn"
                    value={formData.soDienThoai}
                    onChange={e => handleInputChange('soDienThoai', e.target.value)}
                    disabled={isLoading}
                    required
                  />
                  {errors.soDienThoai && (
                    <div className="form-error">
                      <span className="form-error-icon">⚠️</span>
                      <span>{errors.soDienThoai}</span>
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label className="form-label optional">Link Shop</label>
                  <input
                    type="text"
                    className="checkin-input"
                    placeholder="Nhập link shop của bạn (tùy ý)"
                    value={formData.linkShop}
                    onChange={e => handleInputChange('linkShop', e.target.value)}
                    disabled={isLoading}
                  />
                  {errors.linkShop && (
                    <div className="form-error">
                      <span className="form-error-icon">⚠️</span>
                      <span>{errors.linkShop}</span>
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label className="form-label optional">Email</label>
                  <div className="form-help-text">Để mở quyền sử dụng file</div>
                  <input
                    type="email"
                    className="checkin-input"
                    placeholder="example@gmail.com"
                    value={formData.email}
                    onChange={e => handleInputChange('email', e.target.value)}
                    disabled={isLoading}
                  />
                  {errors.email && (
                    <div className="form-error">
                      <span className="form-error-icon">⚠️</span>
                      <span>{errors.email}</span>
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label className="form-label">Vấn đề đang gặp phải</label>
                  <textarea
                    className="checkin-input textarea"
                    placeholder="Câu trả lời của bạn"
                    value={formData.vanDeGapPhai}
                    onChange={e => handleInputChange('vanDeGapPhai', e.target.value)}
                    disabled={isLoading}
                    required
                    rows={3}
                  />
                  {errors.vanDeGapPhai && (
                    <div className="form-error">
                      <span className="form-error-icon">⚠️</span>
                      <span>{errors.vanDeGapPhai}</span>
                    </div>
                  )}
                </div>
                
                <button type="submit" className="checkin-btn" disabled={isLoading}>
                  {isLoading ? 'Đang gửi...' : 'GỬI ĐĂNG KÝ'}
                </button>
              </form>
            </>
          ) : (
            <div className="success-message">
              <h4 className="checkin-title">Đăng ký thành công!</h4>
              <div className="success-content">
                <p>🎉 Cảm ơn bạn đã đăng ký sử dụng tài liệu miễn phí!</p>
                <p>Thông tin của bạn đã được lưu vào hệ thống. TUKI sẽ liên hệ hỗ trợ 1:1 sớm nhất có thể.</p>
                <button 
                  type="button" 
                  className="checkin-btn" 
                  onClick={handleResetForm}
                >
                  ĐĂNG KÝ LẠI
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Robot Image Section */}
        <div className="robot-image-section">
          <img 
            src="/assets/RobotEcommerce.png" 
            alt="Robot AI" 
            className="robot-image" 
          />
        </div>

        {/* Event Details */}
        <div className="event-details">
          <div className="event-date">
            <div className="date-main">17.09.2025</div>
          </div>
          <div className="event-separator"></div>
          <div className="event-day-time">
            <div className="day-of-week">Thứ Tư</div>
            <div className="time-range">7h30 - 17h30</div>
          </div>
          <div className="event-separator"></div>
          <div className="event-location">
            <div className="location-main">Trống Đồng Palace</div>
            <div className="location-address">
              489 Hoàng Quốc Việt, p.Nghĩa Đô, Hà Nội
            </div>
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
          toastStyle={{
            background: 'rgba(15, 23, 42, 0.95)',
            color: '#fff',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '10px',
            backdropFilter: 'blur(10px)',
            fontSize: '14px',
          }}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            left: 'auto',
            bottom: 'auto',
            zIndex: 10000,
            transform: 'none',
          }}
        />
    </div>
    </Router>
  );
}

export default App;
